import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, SlidersHorizontal, Eye, EyeOff, Loader2, Check, AlertTriangle, Layers, MapPin, Calendar } from 'lucide-react'
import { useWS } from '../contexts/WSContext'
import MapUploadWizard from './MapUploadWizard'
import { useConfirm, deleteJSON } from './ConfirmDialog'

// Chip de status por tile: processing (com %), processado, aparece no mapa, falhou.
function StatusChip({ m }) {
  const { t } = useTranslation()
  if (m.status === 'processing')
    return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-status-stop/10 text-status-stop"><Loader2 size={12} className="animate-spin" /> {t('admin:maps.processing', { progress: m.progress || 0 })}</span>
  if (m.status === 'failed')
    return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-status-down/10 text-status-down"><AlertTriangle size={12} /> {t('admin:maps.failed')}</span>
  if (m.active)
    return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-status-op/10 text-status-op"><Eye size={12} /> {t('admin:maps.on_map')}</span>
  return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"><Check size={12} /> {t('admin:maps.processed')}</span>
}

export default function MapsAdminView() {
  const { t } = useTranslation()
  // Gate na ENTRADA do wizard, não dentro dele: um ortomosaico tem centenas de MB e o
  // nginx aceita até 2 GB, então deixar abrir significaria subir o arquivo inteiro pela
  // rede da mina para o servidor recusar com 403 no fim.
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { maps, mines, reloadMaps } = useWS()
  const [wizardOpen, setWizardOpen] = useState(false)
  const [editMap, setEditMap] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  useEffect(() => { reloadMaps() }, [reloadMaps])

  const mineName = (id) => mines.find(m => String(m.id) === String(id))?.name || '—'

  const openNew = () => { setEditMap(null); setWizardOpen(true) }
  const openEdit = (m) => { setEditMap(m); setWizardOpen(true) }
  const toggleActive = (m) => {
    fetch(`/api/maps/${m.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !m.active }) })
      .then(() => reloadMaps()).catch(() => {})
  }
  const remove = (m) => ask({
    title: t('common:actions.delete'),
    message: t('admin:maps.delete_confirm', { name: m.name }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/maps/${m.id}`); reloadMaps() },
  })

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('admin:maps.count', { count: maps.length })}
        </p>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openNew} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16} /> {t('admin:maps.add')}
        </button>
      </div>

      {maps.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openNew} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Layers size={28} />
          <span className="text-sm">{t('admin:maps.empty_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {maps.map(m => (
            <div key={m.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{m.name}</p>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <StatusChip m={m} />
                    {m.status === 'ready' && (
                      <label className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 cursor-pointer select-none" title={t('admin:maps.toggle_title')}>
                        <input type="checkbox" checked={m.active} onChange={() => toggleActive(m)} className="accent-[#017f96]" />
                        {t('admin:maps.show_on_map')}
                      </label>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button disabled={!mayEdit} onClick={() => openEdit(m)} title={!mayEdit ? t(reasonEdit) : t('admin:maps.adjust_bounds')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><SlidersHorizontal size={15} /></button>
                  <button disabled={!mayDelete} onClick={() => remove(m)} title={!mayDelete ? t(reasonDelete) : t('admin:actions.remove')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                </div>
              </div>

              {m.status === 'processing' && (
                <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div className="h-full bg-status-stop transition-all" style={{ width: `${m.progress || 5}%` }} />
                </div>
              )}
              {m.status === 'failed' && m.error && (
                <p className="mt-2 text-[11px] text-status-down line-clamp-2">{m.error}</p>
              )}

              <div className="mt-3 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                <p className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-400 dark:text-slate-500" /> {mineName(m.mine_id)}</p>
                {m.survey_date && <p className="flex items-center gap-1.5"><Calendar size={12} className="text-slate-400 dark:text-slate-500" /> {m.survey_date}</p>}
                {m.status === 'ready' && m.min_zoom != null && (
                  <p className="flex items-center gap-1.5"><Layers size={12} className="text-slate-400 dark:text-slate-500" /> {t('admin:maps.zoom_range', { min: m.min_zoom, max: m.max_zoom })}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <MapUploadWizard key={`map-${editMap?.id ?? 'new'}-${wizardOpen}`} open={wizardOpen} onClose={() => setWizardOpen(false)} editMap={editMap} />
      {confirmDialog}
    </div>
  )
}
